import { lazy } from 'react'

/** Config schema used to add a campaign route in App.js and a link in HomePage.js */
export interface CampaignConfig {
  /** The path to the campaign, used in zion-router’s Route */
  path: string
  /** (optional) The path, excluding any params, used for the HomePage link */
  basePath?: string
  /** Whether the campaign supports all I10 languages */
  allLangsSupported: boolean
  /** (optional) Any specific languages (besides English) supported by the campaign */
  otherLangsSupported?: string
  /** (optional) Whether the campaign will be hidden in non-religious mode */
  isReligious?: boolean
  /** (optional) Whether to authenticate the user before loading the page */
  authRoute?: boolean
  /** The component to render when signed in */
  component: React.LazyExoticComponent<React.ComponentType>
  /** (optional) The component to render when signed out */
  loggedOutComponent?: React.LazyExoticComponent<React.ComponentType>
}

/** Add new campaigns to this array */
const campaignList: CampaignConfig[] = [
  {
    path: '/addphotos/:pid?',
    basePath: '/addphotos',
    allLangsSupported: true,
    component: lazy(() => import('./components/addphotos/AddPhotos')),
    loggedOutComponent: lazy(() => import('./components/addphotos/AddPhotosLoggedOut')),
  },
  {
    path: '/ancestor-origins',
    allLangsSupported: true,
    component: lazy(() => import('./components/ancestor-origins/AncestorOrigins')),
    loggedOutComponent: lazy(() => import('./components/ancestor-origins/AncestorOriginsLoggedOut')),
  },
  {
    // Workaround to support /en/america250, since the language prefix handling doesn't match this subjoined path
    path: '/:languageCode?/america250',
    basePath: '/america250',
    allLangsSupported: false,
    component: lazy(() => import('./components/america250/America250')),
    loggedOutComponent: lazy(() => import('./components/america250/America250')),
  },
  {
    path: '/building-the-church/:categoryId?/:section?/:pid?',
    basePath: '/building-the-church',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/building-the-church/BuildingTheChurchLoggedIn')),
    loggedOutComponent: lazy(() => import('./components/building-the-church/BuildingTheChurchLoggedOut')),
  },
  {
    path: '/calendar/:pid?',
    basePath: '/calendar',
    allLangsSupported: true,
    component: lazy(() => import('./components/calendar/Calendar')),
    loggedOutComponent: lazy(() => import('./components/calendar/CalendarLoggedOut')),
  },
  {
    path: '/cemetery',
    allLangsSupported: true,
    component: lazy(() => import('./components/cemetery/Cemetery')),
    loggedOutComponent: lazy(() => import('./components/cemetery/CemeteryLoggedOut')),
  },
  {
    path: '/challenging-times/:pid?',
    basePath: '/challenging-times',
    allLangsSupported: true,
    component: lazy(() => import('./components/challenging-times/ChallengingTimes')),
    loggedOutComponent: lazy(() => import('./components/challenging-times/ChallengingTimesLoggedOut')),
  },
  {
    path: '/contributors',
    allLangsSupported: true,
    component: lazy(() => import('./components/contributors/Contributors')),
    loggedOutComponent: lazy(() => import('./components/contributors/ContributorsLoggedOut')),
  },
  {
    path: '/discovery-prototype',
    allLangsSupported: false,
    component: lazy(() => import('./components/discovery/Discovery')),
    loggedOutComponent: lazy(() => import('./components/discovery/DiscoveryLoggedOut')),
  },
  {
    path: '/family-ordinance-opportunities/:basePid?',
    basePath: '/family-ordinance-opportunities',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/family-ordinance-opportunities/FamilyOrdinanceOpportunitiesPage')),
    loggedOutComponent: lazy(
      () => import('./components/family-ordinance-opportunities/FamilyOrdinanceOpportunitiesLoggedOut')
    ),
  },
  {
    path: '/family-temple-hints',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/family-temple-hints/FamilyTempleHints')),
    loggedOutComponent: lazy(() => import('./components/family-temple-hints/FamilyTempleHintsLoggedOut')),
  },
  {
    path: '/femalepioneer',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/femalepioneer/FemalePioneer')),
    loggedOutComponent: lazy(() => import('./components/femalepioneer/FemalePioneerLoggedOut')),
  },
  {
    path: '/femalestories/:pid?',
    basePath: '/femalestories',
    allLangsSupported: true,
    component: lazy(() => import('./components/femalestories/FemaleStories')),
    loggedOutComponent: lazy(() => import('./components/femalestories/FemaleStoriesLoggedOut')),
  },
  {
    path: '/hints',
    allLangsSupported: true,
    component: lazy(() => import('./components/recordhints/RecordHints')),
    loggedOutComponent: lazy(() => import('./components/recordhints/RecordHintsLoggedOut')),
  },
  {
    path: '/hints-1950census',
    allLangsSupported: false,
    component: lazy(() => import('./components/hints-1950census/Hints1950Census')),
    loggedOutComponent: lazy(() => import('./components/hints-1950census/Hints1950CensusLoggedOut')),
  },
  {
    path: '/hints-global',
    allLangsSupported: true,
    component: lazy(() => import('./components/hints-global/HintsGlobal')),
    loggedOutComponent: lazy(() => import('./components/hints-global/HintsGlobal')),
  },
  {
    path: '/homelands',
    allLangsSupported: true,
    component: lazy(() => import('./components/homelands/Homelands')),
    loggedOutComponent: lazy(() => import('./components/homelands/HomelandsLoggedOut')),
  },
  {
    path: '/infographic',
    allLangsSupported: true,
    component: lazy(() => import('./components/infographic/Infographic')),
    loggedOutComponent: lazy(() => import('./components/infographic/InfographicLoggedOut')),
  },
  {
    // Workaround to support /en/inspire, since the language prefix handling doesn’t match this subjoined path
    path: '/:languageCode?/inspire',
    basePath: '/inspire',
    allLangsSupported: true,
    isReligious: false,
    component: lazy(() => import('./components/inspire/CampaignLaunch')),
    loggedOutComponent: lazy(() => import('./components/inspire/CampaignLaunchLoggedOut')),
  },
  {
    path: '/journeyoffaith/:pid?',
    basePath: '/journeyoffaith',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/journeyoffaith/JourneyofFaith')),
    loggedOutComponent: lazy(() => import('./components/journeyoffaith/JourneyofFaithLoggedOut')),
  },
  {
    path: '/josephsmithpapers',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/josephsmithpapers/JosephSmithPapers')),
    loggedOutComponent: lazy(() => import('./components/josephsmithpapers/JosephSmithPapersLoggedOut')),
  },
  {
    path: '/juneteenth/:pid?',
    basePath: '/juneteenth',
    allLangsSupported: false,
    component: lazy(() => import('./components/juneteenth/Juneteenth')),
    loggedOutComponent: lazy(() => import('./components/juneteenth/JuneteenthLoggedOut')),
  },
  {
    path: '/learn1',
    allLangsSupported: true,
    component: lazy(() => import('./components/learn1/AncestorQuiz')),
    loggedOutComponent: lazy(() => import('./components/learn1/AncestorQuizLoggedOut')),
  },
  {
    path: '/lookalike',
    allLangsSupported: true,
    component: lazy(() => import('./components/lookalike/LookAlikes')),
    loggedOutComponent: lazy(() => import('./components/lookalike/LookAlikes')),
  },
  {
    path: '/marriages',
    allLangsSupported: true,
    component: lazy(() => import('./components/marriages/Marriages')),
    loggedOutComponent: lazy(() => import('./components/marriages/MarriagesLoggedOut')),
  },
  {
    path: '/mayflower/:attribute?',
    basePath: '/mayflower',
    allLangsSupported: false,
    component: lazy(() => import('./components/mayflower/Mayflower')),
    loggedOutComponent: lazy(() => import('./components/mayflower/MayflowerLoggedOut')),
  },
  {
    path: '/memorial',
    allLangsSupported: true,
    component: lazy(() => import('./components/memorial/Memorial')),
    loggedOutComponent: lazy(() => import('./components/memorial/MemorialLoggedOut')),
  },
  {
    path: '/militaryrecords',
    allLangsSupported: true,
    component: lazy(() => import('./components/militaryrecords/MilitaryRecords')),
    loggedOutComponent: lazy(() => import('./components/militaryrecords/MilitaryRecordsLoggedOut')),
  },
  {
    path: '/missing-data-hints',
    allLangsSupported: false,
    component: lazy(() => import('./components/missing-data-hints/MissingDataHints')),
    loggedOutComponent: lazy(() => import('./components/missing-data-hints/MissingDataHintsLoggedOut')),
  },
  {
    path: '/missing-temple-information',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/missing-temple-information/MissingTempleInformation')),
    loggedOutComponent: lazy(() => import('./components/missing-temple-information/MissingTempleInformationLoggedOut')),
  },
  {
    path: '/missionary',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/missionary-old/Missionary')),
    loggedOutComponent: lazy(() => import('./components/missionary-old/MissionaryLoggedOut')),
  },
  {
    path: '/missionary-new',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/missionary/Missionary')),
    loggedOutComponent: lazy(() => import('./components/missionary/Missionary')),
  },
  {
    path: '/mymatch',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/mymatch/MyMatch')),
    loggedOutComponent: lazy(() => import('./components/mymatch/MyMatch')),
  },
  {
    path: '/mytemple',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/mytemple/MyTemple')),
    loggedOutComponent: lazy(() => import('./components/mytemple/MyTempleLoggedOut')),
  },
  {
    path: '/my-team',
    allLangsSupported: false,
    component: lazy(() => import('./components/world-game/WorldGame')),
    loggedOutComponent: lazy(() => import('./components/world-game/WorldGame')),
  },
  {
    path: '/nauvoo',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/nauvoo/Nauvoo')),
    loggedOutComponent: lazy(() => import('./components/nauvoo/NauvooLoggedOut')),
  },
  {
    path: '/newmemories',
    allLangsSupported: true,
    component: lazy(() => import('./components/newmemories/NewMemoriesPage')),
    loggedOutComponent: lazy(() => import('./components/newmemories/NewMemoriesLoggedOut')),
  },
  {
    path: '/obituaries',
    allLangsSupported: true,
    component: lazy(() => import('./components/obituaries/Obituaries')),
    loggedOutComponent: lazy(() => import('./components/obituaries/ObituariesLoggedOut')),
  },
  {
    path: '/occupations',
    allLangsSupported: false,
    component: lazy(() => import('./components/occupations/Occupations')),
    loggedOutComponent: lazy(() => import('./components/occupations/OccupationsLoggedOut')),
  },
  {
    path: '/ofrenda',
    allLangsSupported: false,
    otherLangsSupported: 'Spanish, Portuguese',
    isReligious: false,
    component: lazy(() => import('./components/ofrenda/Ofrenda')),
    loggedOutComponent: lazy(() => import('./components/ofrenda/Ofrenda')),
  },
  {
    path: '/pearlharbor/:pid?',
    basePath: '/pearlharbor',
    allLangsSupported: false,
    component: lazy(() => import('./components/pearlharbor/PearlHarbor')),
    loggedOutComponent: lazy(() => import('./components/pearlharbor/PearlHarborLoggedOut')),
  },
  {
    path: '/photocollage',
    allLangsSupported: true,
    component: lazy(() => import('./components/photocollage/PhotoCollage')),
    loggedOutComponent: lazy(() => import('./components/photocollage/PhotoCollage')),
  },
  {
    path: '/pioneerchild/:attribute?',
    basePath: '/pioneerchild',
    allLangsSupported: false,
    component: lazy(() => import('./components/pioneerchild/PioneerChild')),
    loggedOutComponent: lazy(() => import('./components/pioneerchild/PioneerChildLoggedOut')),
  },
  {
    path: '/pioneerchildrensmemorial',
    allLangsSupported: false,
    component: lazy(() => import('./components/pioneerchildrensmemorial/PioneerChildrensMemorial')),
    loggedOutComponent: lazy(() => import('./components/pioneerchildrensmemorial/PioneerChildrensMemorial')),
  },
  {
    path: '/pioneers',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/pioneers/Pioneers')),
    loggedOutComponent: lazy(() => import('./components/pioneers/PioneersLoggedOut')),
  },
  {
    path: '/record-discovery',
    allLangsSupported: true,
    component: lazy(() => import('./components/record-discovery/RecordDiscovery')),
    loggedOutComponent: lazy(() => import('./components/record-discovery/RecordDiscoveryLoggedOut')),
  },
  {
    path: '/reliefsociety/:pid?',
    basePath: '/reliefsociety',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/reliefsociety-old/ReliefSociety')),
    loggedOutComponent: lazy(() => import('./components/reliefsociety-old/ReliefSocietyLoggedOut')),
  },
  {
    path: '/religiousrecords/:pid?',
    basePath: '/religiousrecords',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/religiousrecords/ReligiousRecords')),
    loggedOutComponent: lazy(() => import('./components/religiousrecords/ReligiousRecordsLoggedOut')),
  },
  {
    path: '/remaining-ordinances',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/remaining-ordinances/RemainingOrdinances')),
    loggedOutComponent: lazy(() => import('./components/remaining-ordinances/RemainingOrdinancesLoggedOut')),
  },
  {
    path: '/saintsbysea',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/saintsbysea/SaintsBySea')),
    loggedOutComponent: lazy(() => import('./components/saintsbysea/SaintsBySeaLoggedOut')),
  },
  {
    path: '/temple-easy-share',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/temple-easy-share/TempleEasyShare')),
    authRoute: true,
  },
  {
    path: '/temple-opportunity',
    allLangsSupported: true,
    isReligious: true,
    component: lazy(() => import('./components/temple-opportunity/TempleOpportunity')),
    loggedOutComponent: lazy(() => import('./components/temple-opportunity/TempleOpportunityLoggedOut')),
  },
  {
    path: '/temple-tools',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/temple-tools/TempleTools')),
    loggedOutComponent: lazy(() => import('./components/temple-tools/TempleTools')),
  },
  {
    path: '/veterans/:pid?',
    basePath: '/veterans',
    allLangsSupported: false,
    component: lazy(() => import('./components/veterans/Veterans')),
    loggedOutComponent: lazy(() => import('./components/veterans/VeteransLoggedOut')),
  },
  {
    path: '/view-relationship',
    allLangsSupported: true,
    component: lazy(() => import('./components/view-relationship/ViewRelationshipPage')),
    authRoute: true,
  },
  {
    path: '/war-of-1812',
    allLangsSupported: false,
    component: lazy(() => import('./components/war-of-1812/WarOf1812')),
    loggedOutComponent: lazy(() => import('./components/war-of-1812/WarOf1812LoggedOut')),
  },
  {
    path: '/women-of-faith',
    allLangsSupported: false,
    isReligious: true,
    component: lazy(() => import('./components/women-of-faith/WomenOfFaith')),
    loggedOutComponent: lazy(() => import('./components/women-of-faith/WomenOfFaithLoggedOut')),
  },
  {
    path: '/wwiiancestor',
    allLangsSupported: false,
    component: lazy(() => import('./components/wwiiancestor/WW2Ancestor')),
    loggedOutComponent: lazy(() => import('./components/wwiiancestor/WW2AncestorLoggedOut')),
  },
  {
    path: '/wwidraft',
    allLangsSupported: false,
    component: lazy(() => import('./components/wwidraft/WWIDraft')),
    loggedOutComponent: lazy(() => import('./components/wwidraft/WWIDraftLoggedOut')),
  },
  {
    path: '/wwiservice',
    allLangsSupported: true,
    component: lazy(() => import('./components/wwiservice/WWIService')),
    loggedOutComponent: lazy(() => import('./components/wwiservice/WWIServiceLoggedOut')),
  },
  {
    path: '/year-in-review/lite-preview',
    allLangsSupported: true,
    component: lazy(() => import('./components/year-in-review-2025/lite/YearInReview2025LitePreview')),
    authRoute: true,
  },
  {
    path: '/year-in-review',
    allLangsSupported: true,
    component: lazy(() => import('./components/year-in-review-2025/YearInReview2025')),
    loggedOutComponent: lazy(() => import('./components/year-in-review-2025/YearInReview2025')),
  },
]

export default campaignList
